import React, { useState } from 'react';
import { ScanText, Upload, X, Cpu, AlertCircle, Zap } from 'lucide-react';
import { extractFromScreenshots } from './screenshotExtract';
import { extractFieldsFromOcr } from './ocrFieldExtract';
import './OcrFillModal.css';

const MAX_SHOTS = 4;

/*
  OCR Fill — 100% offline, open-source extraction.
    1. tesseract.js reads the screenshot(s) on-device (vision text extraction).
    2. ocrFieldExtract mines the raw text + reconstructed spec rows with strong
       domain regex to fill each form field accurately.
  No cloud functions, no LLM, no API key, no cost.

  Props:
    onClose         — dismiss the modal
    onApply(result) — { name, specs, mappedFields } (same shape as the AI modal,
                      so the parent's handlePopulate handles both identically)
*/
export default function OcrFillModal({ onClose, onApply }) {
  const [shots, setShots]       = useState([]);   // [{ file, preview }]
  const [isRunning, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatus]  = useState('');
  const [error, setError]       = useState('');

  const addShots = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setShots(prev =>
      [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f) }))].slice(0, MAX_SHOTS)
    );
    setError('');
    e.target.value = '';
  };

  const removeShot = (i) => setShots(prev => prev.filter((_, idx) => idx !== i));

  const run = async () => {
    if (!shots.length || isRunning) return;
    setRunning(true);
    setError('');
    setProgress(0);

    try {
      setStatus('Reading your screenshots on-device…');
      const ocr = await extractFromScreenshots(
        shots.map(s => s.file),
        (pct) => setProgress(pct)
      );

      setStatus('Extracting & structuring specifications…');
      const { name, fields } = extractFieldsFromOcr({
        name: ocr.name,
        specs: ocr.specs,
        rawText: ocr.text,
      });

      const foundCount = Object.keys(fields).length;
      if (!name && foundCount === 0 && (!ocr.specs || ocr.specs.length === 0)) {
        throw new Error("Couldn't read any product details. Try a sharper, more zoomed-in screenshot of the spec table.");
      }

      setStatus('Done!');
      // Same result shape the AI modal uses → parent merges mappedFields over
      // its own rule-based mapping for anything left blank.
      setTimeout(() => {
        onApply({ name, specs: ocr.specs, mappedFields: fields });
        onClose();
      }, 350);
    } catch (err) {
      setError(err.message || 'OCR failed. Try a clearer screenshot.');
      setRunning(false);
    }
  };

  return (
    <div className="ocr-overlay" onClick={onClose}>
      <div className="ocr-dialog" onClick={e => e.stopPropagation()}>
        <div className="ocr-header">
          <div className="ocr-brand">
            <span className="ocr-brand-icon"><ScanText size={22} /></span>
            <div>
              <h3 className="ocr-brand-name">OCR Fill</h3>
              <p className="ocr-brand-sub">Offline vision extraction · No AI · No internet</p>
            </div>
          </div>
          <button className="ocr-close" onClick={onClose} disabled={isRunning} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {isRunning ? (
          <div className="ocr-scan-stage">
            <div className="ocr-scanner">
              <Cpu size={46} className="ocr-scan-chip" />
              <div className="ocr-scan-line" />
            </div>
            <p className="ocr-status">{statusMsg}</p>
            <div className="ocr-bar-track">
              <div className="ocr-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <p className="ocr-substatus">{progress}%</p>
          </div>
        ) : (
          <div className="ocr-body">
            {error && (
              <div className="ocr-alert">
                <AlertCircle size={15} /> <span>{error}</span>
              </div>
            )}

            <label className="ocr-dropzone">
              <input type="file" accept="image/*" multiple hidden onChange={addShots} />
              <Upload size={22} className="ocr-dropzone-icon" />
              <span className="ocr-dropzone-label">Click to add screenshot(s)</span>
              <span className="ocr-dropzone-hint">Spec table, product page, or listing · up to {MAX_SHOTS}</span>
            </label>

            {shots.length > 0 && (
              <>
                <div className="ocr-shot-grid">
                  {shots.map((s, i) => (
                    <div className="ocr-shot-thumb" key={i}>
                      <img src={s.preview} alt={`shot-${i}`} />
                      <button type="button" className="ocr-shot-remove" onClick={() => removeShot(i)}>✕</button>
                    </div>
                  ))}
                </div>
                <p className="ocr-shot-count">{shots.length} of {MAX_SHOTS} added</p>
              </>
            )}

            <div className="ocr-actions">
              <button type="button" className="ocr-action-btn" disabled={!shots.length} onClick={run}>
                <Zap size={15} /> Extract &amp; Fill
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
